import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles, renderTip } from "../../components/crm/formControls";
import { scGet } from "../../services/supremeCourtApi";

import SelectMenu from "../../components/crm/SelectMenu";
const SEARCH_TIPS = [
  "All fields marked * are required",
  "Solve the captcha exactly as shown",
  "Captcha refreshes on every tab change",
];

const SEARCH_TABS = [
  { id: "party-name", label: "Party Name" },
  { id: "diary-number", label: "Diary Number" },
  { id: "case-number", label: "Case Number" },
  { id: "cnr-number", label: "CNR Number" },
  { id: "aor", label: "AOR Code" },
  { id: "cause-list", label: "Cause List" },
];

// Cause List form options (mirrors https://www.sci.gov.in/cause-list/)
const LIST_TYPES = [
  { value: "daily", label: "Daily Cause List" },
  { value: "other", label: "Other Lists" },
];

const CAUSE_LIST_SEARCH_BY = [
  { value: "all_courts", label: "All Courts" },
  { value: "court", label: "Court No" },
  { value: "judge", label: "Judge Name" },
  { value: "aor_code", label: "AOR" },
  { value: "party_name", label: "Party Name" },
];

// Causelist types differ by List Type. `value` -> causelist_type,
// `slug` -> causelist_type_value (both are sent, mirroring sci.gov.in).
const CAUSE_LIST_TYPES_DAILY = [
  { value: "all", label: "--All--", slug: "" },
  { value: "Misc. Court", label: "Miscellaneous List", slug: "miscellaneous" },
  { value: "Regular Court", label: "Regular List", slug: "regular-court" },
  { value: "Single Judge Court", label: "Single Judge List", slug: "single-judge" },
  { value: "Chamber Court", label: "Chamber List", slug: "chamber" },
  { value: "Review", label: "Review/Curative", slug: "review" },
  { value: "Registrar Court", label: "Registrar", slug: "registrar" },
];

const CAUSE_LIST_TYPES_OTHER = [
  { value: "all", label: "--All--", slug: "" },
  { value: "Misc. Advance List", label: "Misc. Advance List", slug: "advance-list" },
  { value: "Advance Elimination List", label: "Advance Elimination List", slug: "advance-elimination" },
  { value: "Final Elimination List", label: "Final Elimination List", slug: "final-elimination" },
  { value: "Weekly", label: "Weekly", slug: "weekly" },
  { value: "Terminal", label: "Terminal", slug: "terminal" },
];

const MSB_OPTIONS = [
  { value: "main", label: "Main" },
  { value: "suppli", label: "Supplementry" },
  { value: "both", label: "Both" },
];

// Today's date as dd-mm-yyyy (format the SCI cause-list endpoint expects)
function todayDmy() {
  return dmyPlusDays(0);
}

// dd-mm-yyyy for today + n days (used for the auto from/to range on "Other Lists").
function dmyPlusDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

// Convert an <input type="date"> value (yyyy-mm-dd) to dd-mm-yyyy.
function isoToDmy(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
}

// Convert dd-mm-yyyy back to yyyy-mm-dd for an <input type="date"> value.
function dmyToIso(dmy) {
  if (!dmy) return "";
  const m = dmy.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : dmy;
}

const CASE_TYPES = [
  { id: 1, name: "SPECIAL LEAVE PETITION (CIVIL)" },
  { id: 2, name: "SPECIAL LEAVE PETITION (CRIMINAL)" },
  { id: 3, name: "CIVIL APPEAL" },
  { id: 4, name: "CRIMINAL APPEAL" },
  { id: 5, name: "WRIT PETITION (CIVIL)" },
  { id: 6, name: "WRIT PETITION (CRIMINAL)" },
  { id: 7, name: "TRANSFER PETITION (CIVIL)" },
  { id: 8, name: "TRANSFER PETITION (CRIMINAL)" },
  { id: 9, name: "REVIEW PETITION (CIVIL)" },
  { id: 10, name: "REVIEW PETITION (CRIMINAL)" },
  { id: 11, name: "TRANSFERRED CASE (CIVIL)" },
  { id: 12, name: "TRANSFERRED CASE (CRIMINAL)" },
  { id: 13, name: "SPECIAL LEAVE TO PETITION (CIVIL)..." },
  { id: 14, name: "SPECIAL LEAVE TO PETITION (CRIMINAL)..." },
  { id: 15, name: "WRIT TO PETITION (CIVIL)..." },
  { id: 16, name: "WRIT TO PETITION (CRIMINAL)..." },
  { id: 17, name: "ORIGINAL SUIT" },
  { id: 18, name: "DEATH REFERENCE CASE" },
  { id: 19, name: "CONTEMPT PETITION (CIVIL)" },
  { id: 20, name: "CONTEMPT PETITION (CRIMINAL)" },
  { id: 21, name: "TAX REFERENCE CASE" },
  { id: 22, name: "SPECIAL REFERENCE CASE" },
  { id: 23, name: "ELECTION PETITION (CIVIL)" },
  { id: 24, name: "ARBITRATION PETITION" },
  { id: 25, name: "CURATIVE PETITION (CIVIL)" },
  { id: 26, name: "CURATIVE PETITION (CRL)" },
  { id: 27, name: "REF. U/A 317(1)" },
  { id: 28, name: "MOTION (CRL)" },
  { id: 31, name: "DIARYNO AND DIARYYR" },
  { id: 32, name: "SUO MOTO WRIT PETITION (CIVIL)" },
  { id: 33, name: "SUO MOTO WRIT PETITION (CRIMINAL)" },
  { id: 34, name: "SUO MOTO CONTEMPT PETITION (CIVIL)" },
  { id: 35, name: "SUO MOTO CONTEMPT PETITION (CRIMINAL)" },
  { id: 37, name: "REF. U/S 14 RTI" },
  { id: 38, name: "REF. U/S 17 RTI" },
  { id: 39, name: "MISCELLANEOUS APPLICATION" },
  { id: 40, name: "SUO MOTO TRANSFER PETITION (CIVIL)" },
  { id: 41, name: "SUO MOTO TRANSFER PETITION (CRIMINAL)" },
];

const PARTY_TYPES = [
  { value: "any", label: "Any" },
  { value: "P", label: "Petitioner" },
  { value: "R", label: "Respondent" },
];

const PARTY_STATUSES = [
  { value: "P", label: "Pending" },
  { value: "D", label: "Disposed" },
];

function SupremeCourtSearch() {
  const [activeTab, setActiveTab] = useState("party-name");
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaImgUrl, setCaptchaImgUrl] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [supportedYears, setSupportedYears] = useState([]);
  const [courts, setCourts] = useState([]);
  const [judges, setJudges] = useState([]);
  const navigate = useNavigate();

  // Form states for each tab
  const [diaryForm, setDiaryForm] = useState({ diaryNo: "", year: new Date().getFullYear().toString(), captcha: "" });
  const [caseForm, setCaseForm] = useState({ caseType: "", caseNo: "", year: new Date().getFullYear().toString(), captcha: "" });
  const [cnrForm, setCnrForm] = useState({ cnrNo: "", captcha: "" });
  const [aorForm, setAorForm] = useState({ aorCode: "", year: new Date().getFullYear().toString(), partyType: "any", caseStatus: "P", captcha: "" });
  const [partyForm, setPartyForm] = useState({ partyType: "any", partyName: "", year: new Date().getFullYear().toString(), partyStatus: "P", captcha: "" });
  const [causeListForm, setCauseListForm] = useState({
    listType: "daily",
    searchBy: "",
    court: "",
    judge: "",
    aorCode: "",
    partyName: "",
    causelistType: "",
    listingDate: todayDmy(),
    msb: "main",
    captcha: "",
  });

  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setError("");
    setCaptchaImgUrl("");
    setSessionId("");
    try {
      const res = await scGet(`/api/${activeTab}/challenge`, { language: "en" });
      const data = res.body;
      if (data?.captchaImgUrl) {
        setCaptchaImgUrl(data.captchaImgUrl);
      }
      if (data?.sessionId) {
        setSessionId(data.sessionId);
      }
      if (data?.supportedYears && data.supportedYears.length > 0) {
        setSupportedYears(data.supportedYears);
      }
      if (Array.isArray(data?.courts)) {
        setCourts(data.courts);
      }
      if (Array.isArray(data?.judges)) {
        setJudges(data.judges);
      }
    } catch (err) {
      setError("Failed to load captcha. Please try again.");
    } finally {
      setCaptchaLoading(false);
    }
  }, [activeTab]);

  // Fetch captcha on tab change
  useEffect(() => {
    fetchCaptcha();
    setError("");
    // Reset captcha input for active tab form
    switch (activeTab) {
      case "diary-number":
        setDiaryForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-number":
        setCaseForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "cnr-number":
        setCnrForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "aor":
        setAorForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "party-name":
        setPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "cause-list":
        setCauseListForm((prev) => ({ ...prev, captcha: "" }));
        break;
      default:
        break;
    }
  }, [activeTab, fetchCaptcha]);

  const buildSearchPayload = () => {
    switch (activeTab) {
      case "diary-number":
        return { diaryNo: diaryForm.diaryNo, year: diaryForm.year };
      case "case-number": {
        const caseTypeObj = CASE_TYPES.find((ct) => String(ct.id) === String(caseForm.caseType));
        return {
          caseType: caseForm.caseType,
          caseTypeName: caseTypeObj?.name || "",
          caseNo: caseForm.caseNo,
          year: caseForm.year,
        };
      }
      case "cnr-number":
        return { cnrNo: cnrForm.cnrNo };
      case "aor": {
        const partyTypeObj = PARTY_TYPES.find((pt) => pt.value === aorForm.partyType);
        return {
          aorCode: aorForm.aorCode,
          year: aorForm.year,
          partyType: aorForm.partyType,
          partyTypeLabel: partyTypeObj?.label || "",
          caseStatus: aorForm.caseStatus,
        };
      }
      case "party-name": {
        const partyTypeObj = PARTY_TYPES.find((pt) => pt.value === partyForm.partyType);
        const partyStatusObj = PARTY_STATUSES.find((ps) => ps.value === partyForm.partyStatus);
        return {
          partyType: partyForm.partyType,
          partyTypeLabel: partyTypeObj?.label || "",
          partyName: partyForm.partyName,
          year: partyForm.year,
          partyStatus: partyForm.partyStatus,
          partyStatusLabel: partyStatusObj?.label || "",
        };
      }
      default:
        return {};
    }
  };

  const handleCauseListSearch = async () => {
    setLoading(true);
    setError("");

    try {
      const f = causeListForm;
      const typeSet = f.listType === "other" ? CAUSE_LIST_TYPES_OTHER : CAUSE_LIST_TYPES_DAILY;
      const typeObj = typeSet.find((t) => t.value === f.causelistType);

      const params = {
        listType: f.listType,
        searchBy: f.searchBy,
        causelistType: f.causelistType,
        causelistTypeValue: typeObj?.slug || "",
        // SCI always submits court + judge (defaults to the first option); it keys
        // off search_by, so sending the defaults for other searches is harmless.
        court: f.searchBy === "court" ? f.court : (courts[0]?.value || "1"),
        judge: f.searchBy === "judge" ? f.judge : (judges[0]?.value || "271"),
        msb: f.msb,
        captcha: f.captcha,
        sessionId,
        language: "en",
      };

      // Conditional text fields based on "Search By"
      if (f.searchBy === "aor_code") params.aorCode = f.aorCode;
      if (f.searchBy === "party_name") params.partyName = f.partyName;

      // SCI requires listing_date for both Daily and Other lists; "Other Lists"
      // additionally sends an auto from/to window (today .. today+6).
      params.listingDate = f.listingDate;
      if (f.listType === "other") {
        params.listingDateFrom = dmyPlusDays(0);
        params.listingDateTo = dmyPlusDays(6);
      }

      const res = await scGet("/api/cause-list/search", params);
      const data = res.body;

      if (data?.error) {
        setError(data.error);
        fetchCaptcha();
        return;
      }

      const parsed = data?.parsed || {};
      if (parsed.success === false) {
        setError(parsed.message || "No records found. The captcha may be incorrect.");
        fetchCaptcha();
        return;
      }

      navigate("/portal/supremeCourtCauseListResults", {
        state: {
          records: Array.isArray(parsed.records) ? parsed.records : [],
          message: parsed.message || null,
          searchPayload: {
            listType: f.listType,
            searchBy: f.searchBy,
            causelistType: f.causelistType,
            listingDate: f.listingDate,
            msb: f.msb,
          },
        },
      });
    } catch (err) {
      setError(err.message || "Search failed. Please try again.");
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (activeTab === "cause-list") {
      await handleCauseListSearch();
      return;
    }

    setLoading(true);
    setError("");

    try {
      let searchPath = "";
      let searchParams = {};

      switch (activeTab) {
        case "diary-number":
          searchPath = "/api/diary-number/search";
          searchParams = { diaryNo: diaryForm.diaryNo, year: diaryForm.year, captcha: diaryForm.captcha, sessionId, language: "en" };
          break;
        case "case-number":
          searchPath = "/api/case-number/search";
          searchParams = { caseType: caseForm.caseType, caseNo: caseForm.caseNo, year: caseForm.year, captcha: caseForm.captcha, sessionId };
          break;
        case "cnr-number":
          searchPath = "/api/cnr-number/search";
          searchParams = { cnrNo: cnrForm.cnrNo, captcha: cnrForm.captcha, sessionId };
          break;
        case "aor":
          searchPath = "/api/aor/search";
          searchParams = { aorCode: aorForm.aorCode, year: aorForm.year, captcha: aorForm.captcha, sessionId, partyType: aorForm.partyType, caseStatus: aorForm.caseStatus };
          break;
        case "party-name":
          searchPath = "/api/party-name/search";
          searchParams = { partyType: partyForm.partyType, partyName: partyForm.partyName, year: partyForm.year, partyStatus: partyForm.partyStatus, captcha: partyForm.captcha, sessionId };
          break;
        default:
          break;
      }

      const res = await scGet(searchPath, searchParams);
      const data = res.body;

      if (data?.error) {
        setError(data.error);
        fetchCaptcha();
        return;
      }

      navigate("/portal/supremeCourtResults", {
        state: {
          data: data,
          searchType: activeTab,
          sessionId: sessionId,
          searchPayload: buildSearchPayload(),
        },
      });
    } catch (err) {
      setError(err.message || "Search failed. Please try again.");
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const defaultYear = new Date().getFullYear().toString();
    switch (activeTab) {
      case "diary-number":
        setDiaryForm({ diaryNo: "", year: defaultYear, captcha: "" });
        break;
      case "case-number":
        setCaseForm({ caseType: "", caseNo: "", year: defaultYear, captcha: "" });
        break;
      case "cnr-number":
        setCnrForm({ cnrNo: "", captcha: "" });
        break;
      case "aor":
        setAorForm({ aorCode: "", year: defaultYear, partyType: "any", caseStatus: "P", captcha: "" });
        break;
      case "party-name":
        setPartyForm({ partyType: "any", partyName: "", year: defaultYear, partyStatus: "P", captcha: "" });
        break;
      case "cause-list":
        setCauseListForm({
          listType: "daily",
          searchBy: "",
          court: "",
          judge: "",
          aorCode: "",
          partyName: "",
          causelistType: "",
          listingDate: todayDmy(),
          listingDateTo: todayDmy(),
          msb: "main",
          captcha: "",
        });
        break;
      default:
        break;
    }
    fetchCaptcha();
  };

  // Year options from API or fallback
  const yearOptions = supportedYears.length > 0
    ? supportedYears
    : Array.from({ length: 76 }, (_, i) => String(new Date().getFullYear() - i));

  const renderYearSelect = (value, onChange) => (
    <SelectShell>
      <SelectMenu chevron={false} value={value} onChange={(e) => onChange(e.target.value)} className={selectStyles} required>
        {yearOptions.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </SelectMenu>
    </SelectShell>
  );

  // Captcha image + answer input — two cells of the parent grid. Forced onto a
  // fresh row so the pair never splits across rows as tab field counts vary.
  const renderCaptchaBlock = (captchaValue, onCaptchaChange) => (
    <>
      <Field label="Captcha" className="lg:col-start-1">
        <div className="flex items-stretch gap-2">
          <div className="flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-slate-50">
            {captchaLoading ? (
              <span className="flex items-center gap-2 text-sm text-slate-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : captchaImgUrl ? (
              <img
                src={captchaImgUrl}
                alt="CAPTCHA"
                className="max-h-[34px] object-contain"
                onError={() => setError("Failed to load captcha image.")}
              />
            ) : (
              <span className="px-2 text-center text-xs text-slate-400">Failed to load captcha</span>
            )}
          </div>
          <button
            type="button"
            onClick={fetchCaptcha}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-yellow-400 hover:text-yellow-600"
            title="Refresh Captcha"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </Field>

      <Field label="Enter Captcha">
        <input
          type="text"
          value={captchaValue}
          onChange={onCaptchaChange}
          placeholder="Solve Captcha"
          className={inputStyles}
          required
        />
      </Field>
    </>
  );

  // Every tab renders its fields into this grid, then the shared footer actions.
  const renderFormShell = (fields) => (
    <form onSubmit={handleSearch}>
      <div className="p-5 sm:p-6">
        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
        )}
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          {fields}
        </div>
      </div>

      <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {SEARCH_TIPS.map((tip) => (
            <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
              {renderTip(tip)}
            </li>
          ))}
        </ul>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
            disabled={loading}
          >
            Reset
          </button>
          <button
            type="submit"
            className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loading || captchaLoading}
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>
    </form>
  );

  const renderDiaryNumberForm = () => renderFormShell(
    <>
      <Field label="Diary Number" className="sm:col-span-2">
        <input
          type="text"
          value={diaryForm.diaryNo}
          onChange={(e) => setDiaryForm({ ...diaryForm, diaryNo: e.target.value })}
          placeholder="Diary Number"
          className={inputStyles}
          required
        />
      </Field>
      <Field label="Diary Year">
        {renderYearSelect(diaryForm.year, (v) => setDiaryForm({ ...diaryForm, year: v }))}
      </Field>
      {renderCaptchaBlock(diaryForm.captcha, (e) => setDiaryForm({ ...diaryForm, captcha: e.target.value }))}
    </>
  );

  const renderCaseNumberForm = () => renderFormShell(
    <>
      <Field label="Case Type" className="sm:col-span-2">
        <SelectShell>
          <SelectMenu chevron={false}
            value={caseForm.caseType}
            onChange={(e) => setCaseForm({ ...caseForm, caseType: e.target.value })}
            className={selectStyles}
            required
          >
            <option value="" disabled>--Select--</option>
            {CASE_TYPES.map((ct) => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
          </SelectMenu>
        </SelectShell>
      </Field>
      <Field label="Case Number">
        <input
          type="text"
          value={caseForm.caseNo}
          onChange={(e) => setCaseForm({ ...caseForm, caseNo: e.target.value })}
          placeholder="Case Number"
          className={inputStyles}
          required
        />
      </Field>
      <Field label="Case Year">
        {renderYearSelect(caseForm.year, (v) => setCaseForm({ ...caseForm, year: v }))}
      </Field>
      {renderCaptchaBlock(caseForm.captcha, (e) => setCaseForm({ ...caseForm, captcha: e.target.value }))}
    </>
  );

  const renderCnrNumberForm = () => renderFormShell(
    <>
      <Field label="CNR Number" className="sm:col-span-2">
        <input
          type="text"
          value={cnrForm.cnrNo}
          onChange={(e) => setCnrForm({ ...cnrForm, cnrNo: e.target.value })}
          placeholder="CNR Number"
          className={inputStyles}
          required
        />
      </Field>
      {renderCaptchaBlock(cnrForm.captcha, (e) => setCnrForm({ ...cnrForm, captcha: e.target.value }))}
    </>
  );

  const renderAorForm = () => renderFormShell(
    <>
      <Field label="Petitioner / Respondent">
        <SelectShell>
          <SelectMenu chevron={false}
            value={aorForm.partyType}
            onChange={(e) => setAorForm({ ...aorForm, partyType: e.target.value })}
            className={selectStyles}
            required
          >
            <option value="" disabled>--Select--</option>
            {PARTY_TYPES.map((pt) => (
              <option key={pt.value} value={pt.value}>{pt.label}</option>
            ))}
          </SelectMenu>
        </SelectShell>
      </Field>
      <Field label="AOR Code">
        <input
          type="text"
          value={aorForm.aorCode}
          onChange={(e) => setAorForm({ ...aorForm, aorCode: e.target.value })}
          placeholder="AOR Code"
          className={inputStyles}
          required
        />
      </Field>
      <Field label="Case Year">
        {renderYearSelect(aorForm.year, (v) => setAorForm({ ...aorForm, year: v }))}
      </Field>
      <Field label="Status">
        <SelectShell>
          <SelectMenu chevron={false}
            value={aorForm.caseStatus}
            onChange={(e) => setAorForm({ ...aorForm, caseStatus: e.target.value })}
            className={selectStyles}
            required
          >
            <option value="P">Pending</option>
            <option value="D">Disposed</option>
            <option value="any">Any</option>
          </SelectMenu>
        </SelectShell>
      </Field>
      {renderCaptchaBlock(aorForm.captcha, (e) => setAorForm({ ...aorForm, captcha: e.target.value }))}
    </>
  );

  const renderPartyNameForm = () => renderFormShell(
    <>
      <Field label="Petitioner / Respondent">
        <SelectShell>
          <SelectMenu chevron={false}
            value={partyForm.partyType}
            onChange={(e) => setPartyForm({ ...partyForm, partyType: e.target.value })}
            className={selectStyles}
            required
          >
            <option value="" disabled>--Select--</option>
            {PARTY_TYPES.map((pt) => (
              <option key={pt.value} value={pt.value}>{pt.label}</option>
            ))}
          </SelectMenu>
        </SelectShell>
      </Field>
      <Field label="Party's Name">
        <input
          type="text"
          value={partyForm.partyName}
          onChange={(e) => setPartyForm({ ...partyForm, partyName: e.target.value })}
          placeholder="Party's Name"
          className={inputStyles}
          required
        />
      </Field>
      <Field label="Diary Year">
        {renderYearSelect(partyForm.year, (v) => setPartyForm({ ...partyForm, year: v }))}
      </Field>
      <Field label="Status">
        <SelectShell>
          <SelectMenu chevron={false}
            value={partyForm.partyStatus}
            onChange={(e) => setPartyForm({ ...partyForm, partyStatus: e.target.value })}
            className={selectStyles}
            required
          >
            <option value="" disabled>--Select--</option>
            {PARTY_STATUSES.map((ps) => (
              <option key={ps.value} value={ps.value}>{ps.label}</option>
            ))}
          </SelectMenu>
        </SelectShell>
      </Field>
      {renderCaptchaBlock(partyForm.captcha, (e) => setPartyForm({ ...partyForm, captcha: e.target.value }))}
    </>
  );

  const renderRadioField = (label, name, options, value, onChange, className = "") => (
    <Field label={label} className={className}>
      <div className="flex h-10 flex-wrap items-center gap-4">
        {options.map((opt) => (
          <label key={opt.value} className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700">
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="accent-yellow-500"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </Field>
  );

  const renderCauseListForm = () => {
    const f = causeListForm;
    const set = (patch) => setCauseListForm((prev) => ({ ...prev, ...patch }));
    const typeOptions = f.listType === "other" ? CAUSE_LIST_TYPES_OTHER : CAUSE_LIST_TYPES_DAILY;
    return renderFormShell(
      <>
        {renderRadioField("List Type", "cl_list_type", LIST_TYPES, f.listType, (v) => set({ listType: v, causelistType: "" }), "sm:col-span-2")}

        <Field label="Search By">
          <SelectShell>
            <SelectMenu chevron={false} value={f.searchBy} onChange={(e) => set({ searchBy: e.target.value })} className={selectStyles} required>
              <option value="" disabled>--Select--</option>
              {CAUSE_LIST_SEARCH_BY.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </SelectMenu>
          </SelectShell>
        </Field>

        {f.searchBy === "court" && (
          <Field label="Court No">
            <SelectShell>
              <SelectMenu chevron={false} value={f.court} onChange={(e) => set({ court: e.target.value })} className={selectStyles} required>
                <option value="" disabled>--Select--</option>
                {courts.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </SelectMenu>
            </SelectShell>
          </Field>
        )}

        {f.searchBy === "judge" && (
          <Field label="Judge Name">
            <SelectShell>
              <SelectMenu chevron={false} value={f.judge} onChange={(e) => set({ judge: e.target.value })} className={selectStyles} required>
                <option value="" disabled>--Select--</option>
                {judges.map((j) => (
                  <option key={j.value} value={j.value}>{j.label}</option>
                ))}
              </SelectMenu>
            </SelectShell>
          </Field>
        )}

        {f.searchBy === "aor_code" && (
          <Field label="AOR Code">
            <input
              type="text"
              value={f.aorCode}
              onChange={(e) => set({ aorCode: e.target.value })}
              placeholder="AOR Code"
              className={inputStyles}
              required
            />
          </Field>
        )}

        {f.searchBy === "party_name" && (
          <Field label="Party's Name">
            <input
              type="text"
              value={f.partyName}
              onChange={(e) => set({ partyName: e.target.value })}
              placeholder="Party's Name"
              className={inputStyles}
              required
            />
          </Field>
        )}

        <Field label="Causelist Type">
          <SelectShell>
            <SelectMenu chevron={false} value={f.causelistType} onChange={(e) => set({ causelistType: e.target.value })} className={selectStyles} required>
              <option value="" disabled>--Select--</option>
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </SelectMenu>
          </SelectShell>
        </Field>

        <Field label="Listing Date">
          <input
            type="date"
            value={dmyToIso(f.listingDate)}
            onChange={(e) => set({ listingDate: isoToDmy(e.target.value) })}
            className={inputStyles}
            required
          />
        </Field>

        {renderRadioField("Main/Supplementry", "cl_msb", MSB_OPTIONS, f.msb, (v) => set({ msb: v }), "sm:col-span-2")}

        {renderCaptchaBlock(f.captcha, (e) => set({ captcha: e.target.value }))}
      </>
    );
  };

  const renderActiveForm = () => {
    switch (activeTab) {
      case "diary-number": return renderDiaryNumberForm();
      case "case-number": return renderCaseNumberForm();
      case "cnr-number": return renderCnrNumberForm();
      case "aor": return renderAorForm();
      case "party-name": return renderPartyNameForm();
      case "cause-list": return renderCauseListForm();
      default: return null;
    }
  };

  // Get the active tab label for the heading
  const activeTabLabel = SEARCH_TABS.find((t) => t.id === activeTab)?.label || "";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Supreme Court</span>
        </div>
        <div className="mt-4"><FindCaseStepper current={2} /></div>

        {/* Form Card — full width */}
        <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Search By pills */}
          <div className="border-b border-slate-100 p-5 sm:p-6">
            <p className="mb-2 text-xs font-medium text-slate-600">Search By</p>
            <div className="flex flex-wrap gap-2">
              {SEARCH_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-yellow-500 text-slate-900 shadow-sm"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <h2 className="mt-5 text-sm font-semibold text-slate-800">
              Supreme Court : Search by {activeTabLabel}
            </h2>
          </div>

          {/* Active Form */}
          {renderActiveForm()}
        </div>
      </main>
    </div>
  );
}

export default SupremeCourtSearch;
